import React, { useMemo, memo } from 'react';
import { TrendingUp } from 'lucide-react';

export interface ComplexityChartProps {
  timeComplexity: string;
  currentN?: number;
}

type GrowthFn = (n: number) => number;

const GROWTH_FUNCTIONS: Record<string, GrowthFn> = {
  'O(1)':        () => 1,
  'O(log n)':    (n) => Math.log2(n + 1),
  'O(n)':        (n) => n,
  'O(n log n)':  (n) => n * Math.log2(n + 1),
  'O(n²)':       (n) => n * n,
  'O(n^2)':      (n) => n * n,
  'O(2^n)':      (n) => Math.pow(2, n),
  'O(n!)':       (n) => n <= 10 ? factorial(n) : Infinity,
};

function factorial(n: number): number {
  let r = 1; for (let i = 2; i <= n; i++) r *= i; return r;
}

function resolveComplexity(raw: string): string {
  const s = raw.toLowerCase().replace(/\s/g, '');
  if (s.includes('1)'))                                          return 'O(1)';
  if (s.includes('logn)') && !s.includes('n*') && !s.includes('nlog')) return 'O(log n)';
  if (s.includes('nlogn') || s.includes('n*log'))                return 'O(n log n)';
  if (s.includes('n²') || s.includes('n^2') || s.includes('n*n')) return 'O(n²)';
  if (s.includes('2^n'))                                         return 'O(2^n)';
  if (s.includes('n!)'))                                         return 'O(n!)';
  if (s.includes('n)'))                                          return 'O(n)';
  return 'O(n log n)';
}

interface Curve { label: string; fn: GrowthFn; color: string }

const REFERENCE_CURVES: Curve[] = [
  { label: 'O(log n)',   fn: GROWTH_FUNCTIONS['O(log n)']!,   color: '#22c55e' },
  { label: 'O(n)',       fn: GROWTH_FUNCTIONS['O(n)']!,       color: '#3b82f6' },
  { label: 'O(n log n)', fn: GROWTH_FUNCTIONS['O(n log n)']!, color: '#f59e0b' },
  { label: 'O(n²)',      fn: GROWTH_FUNCTIONS['O(n²)']!,      color: '#ef4444' },
];

const W    = 260;
const H    = 108;
const PAD  = { top: 8, right: 8, bottom: 22, left: 26 };
const STEPS = 40;
const NS   = Array.from({ length: STEPS }, (_, i) => 1 + (i / (STEPS - 1)) * 63);

function buildPath(fn: GrowthFn, maxVal: number): string {
  return NS.map((n, i) => {
    const raw = fn(n);
    const y   = isFinite(raw) ? Math.min(raw / maxVal, 1) : 1;
    const cx  = PAD.left + (i / (STEPS - 1)) * (W - PAD.left - PAD.right);
    const cy  = PAD.top  + (1 - y)           * (H - PAD.top  - PAD.bottom);
    return `${i === 0 ? 'M' : 'L'}${cx.toFixed(1)},${cy.toFixed(1)}`;
  }).join(' ');
}

export const ComplexityChart: React.FC<ComplexityChartProps> = memo(({
  timeComplexity,
  currentN = 32,
}) => {
  const resolved = resolveComplexity(timeComplexity);

  const maxVal = useMemo(() => {
    let m = 1;
    for (const n of NS)
      for (const c of REFERENCE_CURVES) {
        const v = c.fn(n);
        if (isFinite(v) && v > m) m = v;
      }
    return m;
  }, []);

  const currentColor = useMemo(() => {
    return REFERENCE_CURVES.find(c => c.label === resolved)?.color ?? '#a78bfa';
  }, [resolved]);

  const markerN = Math.max(1, Math.min(64, Math.round(currentN)));
  const markerX = PAD.left + ((markerN - 1) / 63) * (W - PAD.left - PAD.right);

  return (
    <div className="flex flex-col gap-2.5">

      {/* Header */}
      <div className="flex items-center gap-2">
        <TrendingUp className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#3b82f6' }} />
        <span className="text-[10px] font-semibold tracking-[0.1em] uppercase" style={{ color: '#52525b' }}>
          Growth Rate
        </span>
        <span className="ml-auto text-[10px] font-mono font-bold" style={{ color: currentColor }}>
          {resolved}
        </span>
      </div>

      {/* SVG */}
      <div
        className="rounded-lg px-3 pt-3 pb-2"
        style={{
          background: 'rgba(0,0,0,0.22)',
          border: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <svg width="100%" viewBox={`0 0 ${W} ${H}`} className="overflow-visible">
          {/* Grid */}
          {[0.25, 0.5, 0.75, 1].map(frac => {
            const y = PAD.top + (1 - frac) * (H - PAD.top - PAD.bottom);
            return (
              <line key={frac}
                x1={PAD.left} y1={y} x2={W - PAD.right} y2={y}
                stroke="rgba(255,255,255,0.04)" strokeWidth="1"
              />
            );
          })}

          {/* Axes */}
          <line x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={H - PAD.bottom}
            stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
          <line x1={PAD.left} y1={H - PAD.bottom} x2={W - PAD.right} y2={H - PAD.bottom}
            stroke="rgba(255,255,255,0.08)" strokeWidth="1" />

          {/* X-axis label */}
          <text x={W / 2} y={H - 3} textAnchor="middle" fontSize="8" fill="rgba(255,255,255,0.2)">
            input size n
          </text>

          {/* Curves */}
          {REFERENCE_CURVES.map(curve => {
            const isActive = curve.label === resolved;
            return (
              <path
                key={curve.label}
                d={buildPath(curve.fn, maxVal)}
                fill="none"
                stroke={curve.color}
                strokeWidth={isActive ? 2 : 1}
                strokeOpacity={isActive ? 0.92 : 0.16}
                strokeDasharray={isActive ? undefined : '3,3'}
              />
            );
          })}

          {/* n marker */}
          <line
            x1={markerX} y1={PAD.top} x2={markerX} y2={H - PAD.bottom}
            stroke="rgba(255,255,255,0.16)" strokeWidth="1" strokeDasharray="2,3"
          />
          <text
            x={markerX} y={H - PAD.bottom + 11}
            textAnchor="middle" fontSize="7" fill="rgba(255,255,255,0.22)"
          >
            n={markerN}
          </text>
        </svg>

        {/* Legend */}
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
          {REFERENCE_CURVES.map(c => {
            const isActive = c.label === resolved;
            return (
              <div key={c.label} className="flex items-center gap-1.5">
                <span
                  className="inline-block w-2.5 h-0.5 rounded-full flex-shrink-0"
                  style={{ background: c.color, opacity: isActive ? 1 : 0.22 }}
                />
                <span
                  className="text-[10px] font-mono"
                  style={{ color: isActive ? c.color : '#3f3f46' }}
                >
                  {c.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});
